import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Cell,
} from 'recharts'
import { glorifyApiClient } from '../../../services/api/glorify'
import type {
  MarketsOverviewResponse,
  MarketsSectorsResponse,
  MarketsSectorHistoryResponse,
  MarketsIndustriesResponse,
  MarketsIndustryHistoryResponse,
} from '../../../services/api/glorify'
import {
  formatCurrency,
  formatSignedCurrency,
  formatSignedPercent,
  changeClass,
  formatDateShort,
  formatTimestamp,
} from './marketsFormat'
import { md, CHART_GRID, TOOLTIP_STYLE, type SectionError, toSectionError } from './marketsTheme'
import { SectionErrorBanner, StaleChip } from './marketsShared'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}
const isoToday = (): string => new Date().toISOString().slice(0, 10)

const SECTOR_HISTORY_DAYS = 90

// ─── Component ───────────────────────────────────────────────────────────────

export default function MarketsPage(): React.JSX.Element {
  // ── Index overview ────────────────────────────────────────────────────
  const [overview, setOverview] = useState<MarketsOverviewResponse | null>(null)
  const [overviewLoading, setOverviewLoading] = useState(true)
  const [overviewError, setOverviewError] = useState<SectionError | null>(null)

  // ── Sectors ────────────────────────────────────────────────────────────
  const [sectors, setSectors] = useState<MarketsSectorsResponse | null>(null)
  const [sectorsLoading, setSectorsLoading] = useState(true)
  const [sectorsError, setSectorsError] = useState<SectionError | null>(null)
  const [selectedSector, setSelectedSector] = useState<string | null>(null)
  const [sectorHistory, setSectorHistory] = useState<MarketsSectorHistoryResponse | null>(null)
  const [sectorHistoryLoading, setSectorHistoryLoading] = useState(false)
  const [sectorHistoryError, setSectorHistoryError] = useState<SectionError | null>(null)

  // ── Industries ─────────────────────────────────────────────────────────
  const [industries, setIndustries] = useState<MarketsIndustriesResponse | null>(null)
  const [industriesLoading, setIndustriesLoading] = useState(true)
  const [industriesError, setIndustriesError] = useState<SectionError | null>(null)
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null)
  const [industryHistory, setIndustryHistory] = useState<MarketsIndustryHistoryResponse | null>(null)
  const [industryHistoryLoading, setIndustryHistoryLoading] = useState(false)
  const [industryHistoryError, setIndustryHistoryError] = useState<SectionError | null>(null)

  // ── Fetchers ───────────────────────────────────────────────────────────
  const fetchOverview = useCallback(async (): Promise<void> => {
    setOverviewError(null)
    try {
      const resp = await glorifyApiClient.marketsOverview()
      setOverview(resp)
    } catch (err) {
      setOverviewError(toSectionError(err, 'Failed to load market overview'))
    } finally {
      setOverviewLoading(false)
    }
  }, [])

  const fetchSectors = useCallback(async (): Promise<void> => {
    setSectorsError(null)
    try {
      const resp = await glorifyApiClient.marketsSectors()
      setSectors(resp)
      setSelectedSector((prev) => prev ?? resp.sectors[0]?.sector ?? null)
    } catch (err) {
      setSectorsError(toSectionError(err, 'Failed to load sector performance'))
    } finally {
      setSectorsLoading(false)
    }
  }, [])

  const fetchSectorHistory = useCallback(async (sector: string): Promise<void> => {
    setSectorHistoryLoading(true)
    setSectorHistoryError(null)
    try {
      const resp = await glorifyApiClient.marketsSectorHistory(
        sector,
        isoDaysAgo(SECTOR_HISTORY_DAYS),
        isoToday()
      )
      setSectorHistory(resp)
    } catch (err) {
      setSectorHistoryError(toSectionError(err, 'Failed to load sector history'))
    } finally {
      setSectorHistoryLoading(false)
    }
  }, [])

  const fetchIndustries = useCallback(async (): Promise<void> => {
    setIndustriesError(null)
    try {
      const resp = await glorifyApiClient.marketsIndustries()
      setIndustries(resp)
      setSelectedIndustry((prev) => prev ?? resp.industries[0]?.industry ?? null)
    } catch (err) {
      setIndustriesError(toSectionError(err, 'Failed to load industry performance'))
    } finally {
      setIndustriesLoading(false)
    }
  }, [])

  const fetchIndustryHistory = useCallback(async (industry: string): Promise<void> => {
    setIndustryHistoryLoading(true)
    setIndustryHistoryError(null)
    try {
      const resp = await glorifyApiClient.marketsIndustryHistory(
        industry,
        isoDaysAgo(SECTOR_HISTORY_DAYS),
        isoToday()
      )
      setIndustryHistory(resp)
    } catch (err) {
      setIndustryHistoryError(toSectionError(err, 'Failed to load industry history'))
    } finally {
      setIndustryHistoryLoading(false)
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchOverview()
    void fetchSectors()
    void fetchIndustries()
  }, [fetchOverview, fetchSectors, fetchIndustries])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (selectedSector) void fetchSectorHistory(selectedSector)
  }, [selectedSector, fetchSectorHistory])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (selectedIndustry) void fetchIndustryHistory(selectedIndustry)
  }, [selectedIndustry, fetchIndustryHistory])

  const handleRefresh = (): void => {
    setOverviewLoading(true)
    void fetchOverview()
    setSectorsLoading(true)
    void fetchSectors()
    setIndustriesLoading(true)
    void fetchIndustries()
    if (selectedSector) void fetchSectorHistory(selectedSector)
    if (selectedIndustry) void fetchIndustryHistory(selectedIndustry)
  }

  // Same newest-first ordering applies to the sector history series.
  const sectorHistoryPoints = useMemo(
    () => (sectorHistory ? [...sectorHistory.points].sort((a, b) => a.date.localeCompare(b.date)) : []),
    [sectorHistory]
  )

  // FMP returns dozens of industries across multiple exchanges, unsorted —
  // rank by average change so the biggest movers surface first.
  const sortedIndustries = useMemo(
    () => (industries ? [...industries.industries].sort((a, b) => b.average_change - a.average_change) : []),
    [industries]
  )

  // The snapshot repeats an industry once per exchange — dedupe alphabetically
  // for the trailing-history selector.
  const industryNames = useMemo(
    () => (industries ? [...new Set(industries.industries.map((i) => i.industry))].sort() : []),
    [industries]
  )

  const industryHistoryPoints = useMemo(
    () => (industryHistory ? [...industryHistory.points].sort((a, b) => a.date.localeCompare(b.date)) : []),
    [industryHistory]
  )

  return (
    <>
      {/* ── Page header + refresh ────────────────────────────────────── */}
      <div className="gd-page-header gd-page-header--with-action">
        <div className="gd-page-header__text">
          <h1>Market Overview</h1>
          <p>Live index levels, sector and industry performance powered by Financial Modeling Prep.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <button className="gd-btn gd-btn--tonal has-icon" onClick={handleRefresh}>
            <span className="material-icons" aria-hidden="true">refresh</span>
            Refresh
          </button>
          {overview && (
            <span className="gd-body-small" style={{ color: 'var(--md-sys-color-outline)' }}>
              Updated {formatTimestamp(overview.as_of)}
            </span>
          )}
        </div>
      </div>

      {/* ── Index cards ──────────────────────────────────────────────── */}
      <SectionErrorBanner error={overviewError} />
      {overviewLoading ? (
        <div className="gd-empty">
          <div className="gd-spinner" aria-label="Loading indexes" />
        </div>
      ) : overview && overview.indexes.length > 0 ? (
        <>
          {overview.stale && <div style={{ marginBottom: 12 }}><StaleChip /></div>}
          <div className="gd-stats-row">
            {overview.indexes.map((idx) => (
              <div key={idx.symbol} className="gd-card gd-card--elevated">
                <div className="gd-index-card">
                  <span className="gd-index-card__symbol">{idx.symbol}</span>
                  <span className="gd-index-card__name">{idx.name}</span>
                  <span className="gd-index-card__price">{formatCurrency(idx.price)}</span>
                  <span className={changeClass(idx.change)}>
                    {formatSignedCurrency(idx.change)} ({formatSignedPercent(idx.change_percentage)})
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : !overviewError ? (
        <div className="gd-empty">
          <span className="material-icons" aria-hidden="true">show_chart</span>
          <p>No index data available.</p>
        </div>
      ) : null}

      {/* ── Sector performance ───────────────────────────────────────── */}
      <div className="gd-section">
        <h2 className="gd-section__title">Sector Performance</h2>
        <SectionErrorBanner error={sectorsError} />
        {sectorsLoading ? (
          <div className="gd-empty">
            <div className="gd-spinner" aria-label="Loading sectors" />
          </div>
        ) : sectors && sectors.sectors.length > 0 ? (
          <div className="gd-chart-card">
            <div className="gd-chart-card__head">
              <span className="gd-body-medium" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                As of {formatDateShort(sectors.date)}
              </span>
              {sectors.stale && <StaleChip />}
            </div>
            <ResponsiveContainer width="100%" height={Math.max(220, sectors.sectors.length * 34)}>
              <BarChart data={sectors.sectors} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke={CHART_GRID} horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(v: number) => `${v.toFixed(1)}%`}
                  stroke={md.onSurfaceVariant}
                  fontSize={11}
                />
                <YAxis type="category" dataKey="sector" width={170} stroke={md.onSurfaceVariant} fontSize={11} />
                <Tooltip
                  {...TOOLTIP_STYLE}
                  formatter={(value) => [`${Number(value).toFixed(2)}%`, 'Avg change']}
                />
                <Bar dataKey="average_change" isAnimationActive={false} radius={4}>
                  {sectors.sectors.map((s) => (
                    <Cell key={s.sector} fill={s.average_change >= 0 ? md.tertiary : md.error} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : !sectorsError ? (
          <div className="gd-empty">
            <span className="material-icons" aria-hidden="true">bar_chart</span>
            <p>No sector data available.</p>
          </div>
        ) : null}

        {sectors && sectors.sectors.length > 0 && (
          <div className="gd-chart-card" style={{ marginTop: 16 }}>
            <div className="gd-chart-card__head">
              <select
                className="gd-select"
                value={selectedSector ?? ''}
                onChange={(e) => setSelectedSector(e.target.value)}
                aria-label="Select sector for trailing 90-day history"
              >
                {sectors.sectors.map((s) => (
                  <option key={s.sector} value={s.sector}>{s.sector}</option>
                ))}
              </select>
              {sectorHistory?.stale && <StaleChip />}
            </div>
            <SectionErrorBanner error={sectorHistoryError} />
            {sectorHistoryLoading ? (
              <div className="gd-empty">
                <div className="gd-spinner" aria-label="Loading sector history" />
              </div>
            ) : sectorHistory && sectorHistoryPoints.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={sectorHistoryPoints} margin={{ left: 0, right: 16 }}>
                  <CartesianGrid stroke={CHART_GRID} vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d: string) => formatDateShort(d)}
                    stroke={md.onSurfaceVariant}
                    fontSize={11}
                    minTickGap={24}
                  />
                  <YAxis
                    tickFormatter={(v: number) => `${v.toFixed(1)}%`}
                    stroke={md.onSurfaceVariant}
                    fontSize={11}
                    width={48}
                  />
                  <Tooltip
                    {...TOOLTIP_STYLE}
                    labelFormatter={(d) => formatDateShort(String(d))}
                    formatter={(value) => [`${Number(value).toFixed(2)}%`, 'Avg change']}
                  />
                  <ReferenceLine y={0} stroke={md.outlineVariant} />
                  <Line
                    type="monotone"
                    dataKey="average_change"
                    stroke={md.primary}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : !sectorHistoryError ? (
              <div className="gd-empty">
                <p>No trailing history for this sector.</p>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* ── Industry performance ─────────────────────────────────────── */}
      <div className="gd-section">
        <h2 className="gd-section__title">Industry Performance</h2>
        <SectionErrorBanner error={industriesError} />
        {industriesLoading ? (
          <div className="gd-empty">
            <div className="gd-spinner" aria-label="Loading industries" />
          </div>
        ) : industries && sortedIndustries.length > 0 ? (
          <div className="gd-chart-card">
            <div className="gd-chart-card__head">
              <span className="gd-body-medium" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                As of {formatDateShort(industries.date)} · {sortedIndustries.length} industries, ranked by average change
              </span>
              {industries.stale && <StaleChip />}
            </div>
            <div className="gd-table-wrap gd-table-wrap--scroll">
              <table className="gd-table">
                <thead>
                  <tr>
                    <th>Industry</th>
                    <th>Exchange</th>
                    <th>Change %</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedIndustries.map((ind) => (
                    <tr key={`${ind.industry}-${ind.exchange}`}>
                      <td><b>{ind.industry}</b></td>
                      <td className="gd-cell-muted">{ind.exchange}</td>
                      <td className={changeClass(ind.average_change)}>{formatSignedPercent(ind.average_change)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : !industriesError ? (
          <div className="gd-empty">
            <span className="material-icons" aria-hidden="true">bar_chart</span>
            <p>No industry data available.</p>
          </div>
        ) : null}

        {industryNames.length > 0 && (
          <div className="gd-chart-card" style={{ marginTop: 16 }}>
            <div className="gd-chart-card__head">
              <select
                className="gd-select"
                value={selectedIndustry ?? ''}
                onChange={(e) => setSelectedIndustry(e.target.value)}
                aria-label="Select industry for trailing 90-day history"
              >
                {industryNames.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              {industryHistory?.stale && <StaleChip />}
            </div>
            <SectionErrorBanner error={industryHistoryError} />
            {industryHistoryLoading ? (
              <div className="gd-empty">
                <div className="gd-spinner" aria-label="Loading industry history" />
              </div>
            ) : industryHistory && industryHistoryPoints.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={industryHistoryPoints} margin={{ left: 0, right: 16 }}>
                  <CartesianGrid stroke={CHART_GRID} vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d: string) => formatDateShort(d)}
                    stroke={md.onSurfaceVariant}
                    fontSize={11}
                    minTickGap={24}
                  />
                  <YAxis
                    tickFormatter={(v: number) => `${v.toFixed(1)}%`}
                    stroke={md.onSurfaceVariant}
                    fontSize={11}
                    width={48}
                  />
                  <Tooltip
                    {...TOOLTIP_STYLE}
                    labelFormatter={(d) => formatDateShort(String(d))}
                    formatter={(value) => [`${Number(value).toFixed(2)}%`, 'Avg change']}
                  />
                  <ReferenceLine y={0} stroke={md.outlineVariant} />
                  <Line
                    type="monotone"
                    dataKey="average_change"
                    stroke={md.primary}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : !industryHistoryError ? (
              <div className="gd-empty">
                <p>No trailing history for this industry.</p>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  )
}
